/**
 * Diagrama visual de uniones: arrastra una fuente sobre otra o tira un conector.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ink, studio } from '../studioUi';
import { JOIN_TYPES, sourceLabel } from './engineConfig';

export const TYPE_MARK = {
    inner: 'Interna',
    left: 'Izquierda',
    right: 'Derecha',
    outer: 'Completa',
};

export const SOURCE_PALETTE = [
    { fill: '#e8f0fe', stroke: '#1a73e8', text: '#174ea6' },
    { fill: '#e6f4ea', stroke: '#137333', text: '#0d652d' },
    { fill: '#fce8e6', stroke: '#c5221f', text: '#a50e0e' },
    { fill: '#fef7e0', stroke: '#e37400', text: '#b06000' },
    { fill: '#f3e8fd', stroke: '#7b1fa2', text: '#6a1b9a' },
    { fill: '#e0f7fa', stroke: '#00838f', text: '#006064' },
    { fill: '#fce4ec', stroke: '#c2185b', text: '#880e4f' },
    { fill: '#e8eaf6', stroke: '#3949ab', text: '#1a237e' },
];

const JOIN_PALETTE = {
    inner: { fill: '#e6f4ea', stroke: '#137333', text: '#0d652d' },
    left: { fill: '#e8f0fe', stroke: '#1a73e8', text: '#174ea6' },
    right: { fill: '#fff8e1', stroke: '#e37400', text: '#b06000' },
    outer: { fill: '#f4eefc', stroke: '#5E35B1', text: '#4527a0' },
};

export function colorForSource(sourceId, dataSources = [], joins = []) {
    const source = dataSources.find((item) => item.id === sourceId);
    if (source?.kind === 'join') {
        const join = joins.find((item) => item.id === source.joinId);
        return JOIN_PALETTE[join?.type] || JOIN_PALETTE.left;
    }
    const sheets = dataSources.filter((item) => item.kind !== 'join');
    const idx = Math.max(0, sheets.findIndex((item) => item.id === sourceId));
    return SOURCE_PALETTE[idx % SOURCE_PALETTE.length];
}

export function commonJoinFields(leftCols = [], rightCols = []) {
    return guessJoinKeys(leftCols, rightCols).filter((item) => item.left && item.right);
}

function layoutMetrics(expanded) {
    if (expanded) {
        return { nodeW: 228, nodeH: 108, joinW: 240, joinH: 122, colGap: 120, rowGap: 32, pad: 28 };
    }
    return { nodeW: 188, nodeH: 78, joinW: 200, joinH: 92, colGap: 88, rowGap: 22, pad: 20 };
}

function sourceDependsOn(dataSources, joins, sourceId, targetId, seen = new Set()) {
    if (!sourceId || !targetId) return false;
    if (sourceId === targetId) return true;
    if (seen.has(sourceId)) return false;
    seen.add(sourceId);
    const source = dataSources.find((item) => item.id === sourceId);
    if (!source || source.kind !== 'join') return false;
    const join = joins.find((item) => item.id === source.joinId);
    if (!join) return false;
    return sourceDependsOn(dataSources, joins, join.leftDataSourceId, targetId, seen)
        || sourceDependsOn(dataSources, joins, join.rightDataSourceId, targetId, seen);
}

export function wouldCreateJoinCycle(dataSources, joins, leftId, rightId) {
    if (!leftId || !rightId || leftId === rightId) return true;
    return sourceDependsOn(dataSources, joins, leftId, rightId)
        || sourceDependsOn(dataSources, joins, rightId, leftId);
}

export function guessJoinKeys(leftCols = [], rightCols = []) {
    const rightByName = new Map(rightCols.map((col) => [String(col.name).toLowerCase(), col.name]));
    const matches = [];
    leftCols.forEach((col) => {
        const hit = rightByName.get(String(col.name).toLowerCase());
        if (hit) matches.push({ left: col.name, right: hit });
    });
    return matches.length ? matches : [{ left: '', right: '' }];
}

function depthOf(source, dataSources, joins, memo = new Map()) {
    if (!source) return 0;
    if (memo.has(source.id)) return memo.get(source.id);
    memo.set(source.id, 0);
    if (source.kind !== 'join') return 0;
    const join = joins.find((item) => item.id === source.joinId);
    if (!join) return 1;
    const left = dataSources.find((item) => item.id === join.leftDataSourceId);
    const right = dataSources.find((item) => item.id === join.rightDataSourceId);
    const depth = 1 + Math.max(
        depthOf(left, dataSources, joins, memo),
        depthOf(right, dataSources, joins, memo)
    );
    memo.set(source.id, depth);
    return depth;
}

function layoutNodes(dataSources, joins, expanded = false) {
    const { nodeW, nodeH, joinW, joinH, colGap, rowGap, pad } = layoutMetrics(expanded);
    const memo = new Map();
    const withDepth = dataSources.map((source) => ({
        source,
        depth: depthOf(source, dataSources, joins, memo),
        w: source.kind === 'join' ? joinW : nodeW,
        h: source.kind === 'join' ? joinH : nodeH,
    }));
    const columns = new Map();
    withDepth.forEach((item) => {
        if (!columns.has(item.depth)) columns.set(item.depth, []);
        columns.get(item.depth).push(item);
    });
    const positions = {};
    [...columns.keys()].sort((a, b) => a - b).forEach((depth) => {
        const col = columns.get(depth);
        col.forEach((item, index) => {
            positions[item.source.id] = {
                x: pad + depth * (Math.max(nodeW, joinW) + colGap),
                y: pad + index * (Math.max(nodeH, joinH) + rowGap),
                w: item.w,
                h: item.h,
                depth,
            };
        });
    });
    withDepth.filter((item) => item.source.kind === 'join').forEach((item) => {
        const join = joins.find((row) => row.id === item.source.joinId);
        if (!join) return;
        const left = positions[join.leftDataSourceId];
        const right = positions[join.rightDataSourceId];
        if (!left && !right) return;
        const leftMid = left ? left.y + left.h / 2 : 0;
        const rightMid = right ? right.y + right.h / 2 : 0;
        const count = (left ? 1 : 0) + (right ? 1 : 0);
        const midY = count ? (leftMid + rightMid) / count : pad;
        positions[item.source.id] = {
            ...positions[item.source.id],
            y: Math.max(pad, midY - item.h / 2),
        };
    });
    const byDepth = new Map();
    Object.entries(positions).forEach(([id, box]) => {
        const depth = box.depth || 0;
        if (!byDepth.has(depth)) byDepth.set(depth, []);
        byDepth.get(depth).push({ id, box });
    });
    byDepth.forEach((col) => {
        col.sort((a, b) => a.box.y - b.box.y);
        col.forEach((item, index) => {
            if (index === 0) return;
            const prev = col[index - 1].box;
            const minY = prev.y + prev.h + rowGap;
            if (item.box.y < minY) item.box.y = minY;
        });
    });
    const values = Object.values(positions);
    const width = values.reduce((max, box) => Math.max(max, box.x + box.w), 360) + pad;
    const height = values.reduce((max, box) => Math.max(max, box.y + box.h), 180) + pad;
    return { positions, width, height };
}

function port(box, side) {
    if (side === 'right') return { x: box.x + box.w, y: box.y + box.h / 2 };
    return { x: box.x, y: box.y + box.h / 2 };
}

function curve(from, to) {
    const dx = Math.max(40, Math.abs(to.x - from.x) / 2);
    return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`;
}

function hitNode(positions, canvas, clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const localX = clientX - rect.left + canvas.scrollLeft;
    const localY = clientY - rect.top + canvas.scrollTop;
    return Object.entries(positions).find(([, box]) => (
        localX >= box.x && localX <= box.x + box.w && localY >= box.y && localY <= box.y + box.h
    ))?.[0] || null;
}

export default function JoinBlendCanvas({
    dataSources = [],
    joins = [],
    schemasBySource = {},
    activeSourceId,
    loadingColumns,
    onSelectSource,
    onBlend,
    onPatchJoin,
    onTestJoin,
    expanded = false,
    showInspector = true,
    onExpand,
    onRemoveJoin,
}) {
    const canvasRef = useRef(null);
    const linkRef = useRef(null);
    const blendRef = useRef(onBlend);
    const [linkDrag, setLinkDrag] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    blendRef.current = onBlend;
    const { positions, width, height } = useMemo(
        () => layoutNodes(dataSources, joins, expanded),
        [dataSources, joins, expanded]
    );

    const edges = useMemo(() => {
        const lines = [];
        joins.forEach((join) => {
            const joinSource = dataSources.find((item) => item.joinId === join.id);
            const joinBox = joinSource ? positions[joinSource.id] : null;
            if (!joinBox) return;
            const leftBox = positions[join.leftDataSourceId];
            const rightBox = positions[join.rightDataSourceId];
            if (leftBox) {
                lines.push({
                    id: `${join.id}-left`,
                    color: colorForSource(join.leftDataSourceId, dataSources, joins).stroke,
                    d: curve(port(leftBox, 'right'), { x: joinBox.x, y: joinBox.y + joinBox.h * 0.35 }),
                });
            }
            if (rightBox) {
                lines.push({
                    id: `${join.id}-right`,
                    color: colorForSource(join.rightDataSourceId, dataSources, joins).stroke,
                    d: curve(port(rightBox, 'right'), { x: joinBox.x, y: joinBox.y + joinBox.h * 0.65 }),
                });
            }
        });
        return lines;
    }, [joins, dataSources, positions]);

    const startLink = (event, sourceId) => {
        event.preventDefault();
        event.stopPropagation();
        const next = { fromId: sourceId, x: event.clientX, y: event.clientY };
        linkRef.current = next;
        setLinkDrag(next);
    };

    useEffect(() => {
        if (!linkDrag) return undefined;
        const move = (event) => {
            if (!linkRef.current) return;
            linkRef.current = { ...linkRef.current, x: event.clientX, y: event.clientY };
            setLinkDrag({ ...linkRef.current });
            const canvas = canvasRef.current;
            const targetId = canvas ? hitNode(positions, canvas, event.clientX, event.clientY) : null;
            setDropTarget(targetId && targetId !== linkRef.current.fromId ? targetId : null);
        };
        const up = (event) => {
            const canvas = canvasRef.current;
            const fromId = linkRef.current?.fromId;
            const targetId = canvas ? hitNode(positions, canvas, event.clientX, event.clientY) : null;
            linkRef.current = null;
            setLinkDrag(null);
            setDropTarget(null);
            if (fromId && targetId && targetId !== fromId && blendRef.current) {
                blendRef.current(fromId, targetId);
            }
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        return () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
    }, [Boolean(linkDrag), positions]);

    const onCardDragStart = (event, sourceId) => {
        event.dataTransfer.setData('text/source-id', sourceId);
        event.dataTransfer.effectAllowed = 'link';
    };

    const onCardDrop = (event, targetId) => {
        event.preventDefault();
        const fromId = event.dataTransfer.getData('text/source-id');
        setDropTarget(null);
        if (fromId && fromId !== targetId) onBlend(fromId, targetId);
    };

    const tempLine = (() => {
        if (!linkDrag || !canvasRef.current) return null;
        const box = positions[linkDrag.fromId];
        if (!box) return null;
        const rect = canvasRef.current.getBoundingClientRect();
        return curve(port(box, 'right'), {
            x: linkDrag.x - rect.left + canvasRef.current.scrollLeft,
            y: linkDrag.y - rect.top + canvasRef.current.scrollTop,
        });
    })();

    if (dataSources.filter((item) => item.kind !== 'join').length < 2 && !joins.length) {
        return (
            <p style={{ ...studio.hint, margin: '8px 2px 0' }}>
                Conecta al menos dos hojas y arrastra una sobre la otra para unirlas.
            </p>
        );
    }

    return (
        <div style={ui.wrap}>
            <div style={ui.legendRow}>
                <div style={ui.legend}>
                    Arrastra una fuente sobre otra, o tira del punto de color hacia la que quieras unir.
                </div>
                {onExpand && (
                    <button type="button" style={ui.pillAccent} onClick={onExpand}>
                        Abrir mapa completo
                    </button>
                )}
            </div>
            <div style={ui.swatches}>
                {dataSources.filter((item) => item.kind !== 'join').map((source) => {
                    const tone = colorForSource(source.id, dataSources, joins);
                    return (
                        <span
                            key={source.id}
                            style={{
                                ...ui.swatch,
                                backgroundColor: tone.fill,
                                color: tone.text,
                                borderColor: tone.stroke,
                            }}
                        >
                            {sourceLabel(source)}
                        </span>
                    );
                })}
            </div>
            <div
                ref={canvasRef}
                style={{ ...ui.canvas, minHeight: Math.max(expanded ? 280 : 160, height) }}
                onPointerLeave={() => { if (!linkDrag) setDropTarget(null); }}
            >
                <svg width={width} height={Math.max(expanded ? 280 : 160, height)} style={ui.svg}>
                    {edges.map((edge) => (
                        <path key={edge.id} d={edge.d} fill="none" stroke={edge.color || ink.accent} strokeWidth={expanded ? 2.5 : 2} opacity="0.9" />
                    ))}
                    {tempLine && (
                        <path d={tempLine} fill="none" stroke={ink.accent} strokeWidth="2" strokeDasharray="6 4" />
                    )}
                </svg>
                {dataSources.map((source) => {
                    const box = positions[source.id];
                    if (!box) return null;
                    const selected = source.id === activeSourceId;
                    const hover = dropTarget === source.id;
                    const join = source.kind === 'join' ? joins.find((item) => item.id === source.joinId) : null;
                    const tone = colorForSource(source.id, dataSources, joins);
                    const cols = schemasBySource[source.id]?.columns?.length;
                    const rows = schemasBySource[source.id]?.rowCount;
                    const commons = source.kind === 'join'
                        ? commonJoinFields(
                            schemasBySource[join?.leftDataSourceId]?.columns || [],
                            schemasBySource[join?.rightDataSourceId]?.columns || []
                        )
                        : [];
                    return (
                        <div
                            key={source.id}
                            draggable
                            onDragStart={(event) => onCardDragStart(event, source.id)}
                            onDragOver={(event) => { event.preventDefault(); setDropTarget(source.id); }}
                            onDragLeave={() => setDropTarget((current) => (current === source.id ? null : current))}
                            onDrop={(event) => onCardDrop(event, source.id)}
                            onClick={() => onSelectSource(source.id)}
                            style={{
                                ...ui.node,
                                left: box.x,
                                top: box.y,
                                width: box.w,
                                height: box.h,
                                borderColor: hover || selected ? tone.stroke : tone.stroke,
                                borderWidth: selected || hover ? 2 : 1,
                                backgroundColor: hover ? tone.fill : (selected ? tone.fill : ink.surface),
                                boxShadow: selected ? `0 0 0 3px ${tone.fill}` : ui.node.boxShadow,
                            }}
                        >
                            <div style={{ ...ui.nodeKicker, color: tone.text }}>
                                {source.kind === 'join' ? `Unión · ${TYPE_MARK[join?.type] || 'Izquierda'}` : (source.sheet_name || 'Hoja')}
                            </div>
                            <div style={ui.nodeTitle}>{sourceLabel(source)}</div>
                            <div style={ui.nodeMeta}>
                                {cols ? `${cols} cols` : 'Sin esquema'}
                                {rows != null ? ` · ${Number(rows).toLocaleString('es-MX')} filas` : ''}
                            </div>
                            {expanded && commons.length > 0 && (
                                <div style={ui.commonRow}>
                                    {commons.slice(0, 3).map((item) => (
                                        <span key={`${item.left}-${item.right}`} style={{ ...ui.commonPill, color: tone.text, backgroundColor: tone.fill }}>
                                            {item.left}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <button
                                type="button"
                                draggable={false}
                                title="Arrastra hasta otra fuente para unir"
                                onPointerDown={(event) => startLink(event, source.id)}
                                style={{
                                    ...ui.port,
                                    backgroundColor: tone.stroke,
                                    borderColor: ink.surface,
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            {showInspector && (
                <JoinInspector
                    dataSources={dataSources}
                    joins={joins}
                    schemasBySource={schemasBySource}
                    activeSourceId={activeSourceId}
                    loadingColumns={loadingColumns}
                    onPatchJoin={onPatchJoin}
                    onTestJoin={onTestJoin}
                    onRemoveJoin={onRemoveJoin}
                />
            )}
        </div>
    );
}

const ui = {
    wrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginTop: 0,
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    legend: {
        fontSize: '0.7rem',
        color: ink.muted,
        fontWeight: 500,
        lineHeight: 1.35,
        flex: 1,
    },
    legendRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        flexWrap: 'wrap',
    },
    swatches: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
    },
    swatch: {
        fontSize: '0.68rem',
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
    },
    commonRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 6,
    },
    commonPill: {
        fontSize: '0.62rem',
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 999,
    },
    commonBtn: {
        appearance: 'none',
        fontSize: '0.68rem',
        fontWeight: 700,
        padding: '4px 8px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    canvas: {
        position: 'relative',
        overflow: 'auto',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: ink.lineStrong,
        borderRadius: 14,
        minHeight: 140,
        flex: 1,
    },
    svg: {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
    },
    node: {
        position: 'absolute',
        boxSizing: 'border-box',
        padding: '10px 14px 10px 12px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 12,
        boxShadow: '0 6px 18px rgba(28, 29, 33, 0.06)',
        cursor: 'grab',
        overflow: 'visible',
    },
    nodeKicker: {
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: ink.accent,
        marginBottom: 4,
    },
    nodeTitle: {
        fontSize: '0.9rem',
        fontWeight: 700,
        color: ink.text,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    nodeMeta: {
        marginTop: 4,
        fontSize: '0.72rem',
        color: ink.faint,
    },
    port: {
        position: 'absolute',
        right: -8,
        top: '50%',
        width: 16,
        height: 16,
        marginTop: -8,
        borderRadius: '50%',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: ink.surface,
        backgroundColor: ink.accent,
        cursor: 'crosshair',
        padding: 0,
    },
    inspector: {
        padding: '10px 12px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 14,
    },
    inspectorHead: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        marginBottom: 8,
    },
    inspectorTitle: {
        fontSize: '0.65rem',
        fontWeight: 700,
        color: ink.accent,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
    },
    inspectorRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'flex-end',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        minWidth: 140,
    },
    tiny: {
        fontSize: '0.62rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: ink.faint,
        fontWeight: 700,
    },
    input: {
        padding: '4px 12px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        fontSize: '0.76rem',
        minHeight: 30,
        height: 30,
        fontFamily: 'inherit',
        color: ink.text,
        boxSizing: 'border-box',
        width: '100%',
    },
    pillGhost: {
        appearance: 'none',
        padding: '4px 12px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.accentText,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    pillAccent: {
        appearance: 'none',
        padding: '4px 14px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.accent,
        color: ink.surface,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    pillDanger: {
        appearance: 'none',
        padding: '4px 14px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    keyRow: {
        display: 'flex',
        gap: 6,
        alignItems: 'flex-end',
    },
    equals: {
        paddingBottom: 6,
        color: ink.faint,
        fontWeight: 700,
    },
    iconKill: {
        width: 30,
        height: 30,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        borderRadius: 999,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    metaLine: {
        fontSize: '0.7rem',
        color: ink.muted,
        alignSelf: 'center',
        fontWeight: 600,
    },
};

export function JoinInspector({
    dataSources = [],
    joins = [],
    schemasBySource = {},
    activeSourceId,
    loadingColumns,
    onPatchJoin,
    onTestJoin,
    onRemoveJoin,
}) {
    const activeSource = dataSources.find((item) => item.id === activeSourceId) || null;
    const activeJoin = activeSource?.kind === 'join'
        ? joins.find((item) => item.id === activeSource.joinId)
        : null;
    if (!activeJoin) {
        return (
            <p style={{ ...studio.hint, margin: '8px 2px 0' }}>
                Selecciona un nodo de unión en el mapa para editar tipo y claves.
            </p>
        );
    }
    const left = dataSources.find((item) => item.id === activeJoin.leftDataSourceId);
    const right = dataSources.find((item) => item.id === activeJoin.rightDataSourceId);
    const leftTone = colorForSource(activeJoin.leftDataSourceId, dataSources, joins);
    const rightTone = colorForSource(activeJoin.rightDataSourceId, dataSources, joins);
    const leftCols = schemasBySource[activeJoin.leftDataSourceId]?.columns || [];
    const rightCols = schemasBySource[activeJoin.rightDataSourceId]?.columns || [];
    const commons = commonJoinFields(leftCols, rightCols);
    const used = new Set((activeJoin.keys || []).map((key) => `${key.left}::${key.right}`));
    const joinReady = Boolean((activeJoin.keys || []).some((key) => key.left && key.right));
    const resultMeta = schemasBySource[activeSource?.id];
    const applyPair = (pair) => {
        const keys = [...(activeJoin.keys || [])];
        const empty = keys.findIndex((key) => !key.left || !key.right);
        if (empty >= 0) keys[empty] = { ...keys[empty], ...pair };
        else keys.push(pair);
        onPatchJoin(activeJoin.id, { keys });
    };
    return (
        <div style={ui.inspector}>
            <div style={ui.inspectorHead}>
                <strong style={ui.inspectorTitle}>Configurar unión</strong>
                <span style={studio.hint}>
                    <span style={{ color: leftTone.text, fontWeight: 700 }}>{sourceLabel(left)}</span>
                    {'  ⋈  '}
                    <span style={{ color: rightTone.text, fontWeight: 700 }}>{sourceLabel(right)}</span>
                </span>
            </div>
            {commons.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                    <span style={ui.tiny}>Campos en común</span>
                    <div style={{ ...ui.commonRow, marginTop: 4 }}>
                        {commons.map((item) => {
                            const on = used.has(`${item.left}::${item.right}`);
                            return (
                                <button
                                    key={`${item.left}-${item.right}`}
                                    type="button"
                                    disabled={on}
                                    onClick={() => applyPair(item)}
                                    style={{
                                        ...ui.commonBtn,
                                        backgroundColor: on ? leftTone.fill : ink.surface,
                                        color: leftTone.text,
                                        borderColor: leftTone.stroke,
                                        opacity: on ? 0.7 : 1,
                                    }}
                                    title={on ? 'Ya está en las claves' : `Unir ${item.left} = ${item.right}`}
                                >
                                    {item.left} = {item.right}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            style={ui.pillGhost}
                            onClick={() => onPatchJoin(activeJoin.id, { keys: commons })}
                        >
                            Usar coincidencias
                        </button>
                    </div>
                </div>
            )}
            <div style={ui.inspectorRow}>
                <label style={ui.field}>
                    <span style={ui.tiny}>Tipo</span>
                    <select
                        style={ui.input}
                        value={activeJoin.type}
                        onChange={(event) => onPatchJoin(activeJoin.id, { type: event.target.value })}
                    >
                        {JOIN_TYPES.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                    </select>
                </label>
                {(activeJoin.keys || []).map((key, index) => (
                    <div key={`${activeJoin.id}-k-${index}`} style={ui.keyRow}>
                        <label style={ui.field}>
                            <span style={{ ...ui.tiny, color: leftTone.text }}>Clave {sourceLabel(left)}</span>
                            <select
                                style={{ ...ui.input, borderColor: leftTone.stroke }}
                                value={key.left}
                                onChange={(event) => onPatchJoin(activeJoin.id, {
                                    keys: activeJoin.keys.map((row, i) => i === index ? { ...row, left: event.target.value } : row),
                                })}
                            >
                                <option value="">Columna…</option>
                                {leftCols.map((col) => {
                                    const match = commons.some((item) => item.left === col.name);
                                    return <option key={col.name} value={col.name}>{match ? `★ ${col.name}` : col.name}</option>;
                                })}
                            </select>
                        </label>
                        <span style={ui.equals}>=</span>
                        <label style={ui.field}>
                            <span style={{ ...ui.tiny, color: rightTone.text }}>Clave {sourceLabel(right)}</span>
                            <select
                                style={{ ...ui.input, borderColor: rightTone.stroke }}
                                value={key.right}
                                onChange={(event) => onPatchJoin(activeJoin.id, {
                                    keys: activeJoin.keys.map((row, i) => i === index ? { ...row, right: event.target.value } : row),
                                })}
                            >
                                <option value="">Columna…</option>
                                {rightCols.map((col) => {
                                    const match = commons.some((item) => item.right === col.name);
                                    return <option key={col.name} value={col.name}>{match ? `★ ${col.name}` : col.name}</option>;
                                })}
                            </select>
                        </label>
                        {(activeJoin.keys || []).length > 1 && (
                            <button
                                type="button"
                                style={ui.iconKill}
                                onClick={() => onPatchJoin(activeJoin.id, {
                                    keys: activeJoin.keys.filter((_, i) => i !== index),
                                })}
                            >
                                ×
                            </button>
                        )}
                    </div>
                ))}
                <button
                    type="button"
                    style={ui.pillGhost}
                    onClick={() => onPatchJoin(activeJoin.id, {
                        keys: [...(activeJoin.keys || []), { left: '', right: '' }],
                    })}
                >
                    + Clave
                </button>
                <button
                    type="button"
                    style={ui.pillAccent}
                    disabled={loadingColumns || !joinReady}
                    onClick={() => onTestJoin(activeSource.id)}
                >
                    {loadingColumns ? 'Uniendo…' : 'Probar unión'}
                </button>
                {onRemoveJoin ? (
                    <button
                        type="button"
                        style={ui.pillDanger}
                        onClick={() => onRemoveJoin(activeJoin.id)}
                    >
                        Eliminar unión
                    </button>
                ) : null}
                {resultMeta?.columns?.length ? (
                    <span style={ui.metaLine}>
                        {resultMeta.columns.length} columnas · {(resultMeta.rowCount || 0).toLocaleString('es-MX')} filas
                    </span>
                ) : null}
            </div>
            {!leftCols.length || !rightCols.length ? (
                <p style={{ ...studio.hint, margin: '8px 0 0' }}>
                    Conecta las hojas de ambos lados para ver las columnas de la clave.
                </p>
            ) : (!commons.length ? (
                <p style={{ ...studio.hint, margin: '8px 0 0' }}>
                    No hay nombres de columna iguales. Elige las claves a mano.
                </p>
            ) : null)}
        </div>
    );
}
